import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { type FormikProps } from 'formik'
import { debounce } from 'lodash'
import { useSelector } from 'react-redux'

import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import {
  makeSelectContentTypesByModel,
  makeSelectFeatureSwitches,
} from '@webapp/core/stores/global'
import { type GetExpenseRequestFormDataUseCaseResult } from '@webapp/modules/expense/business-logic/use-cases/GetExpenseRequestFormData'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { useGetRequestableApproversPresenter } from '@webapp/modules/expense/features/get-requestable-approvers'

export const useApproverResolution = (
  formikRef: MutableRefObject<FormikProps<ExpenseReportFormSchema> | null>,
  formData: GetExpenseRequestFormDataUseCaseResult | undefined
) => {
  const featureSwitches = useSelector(makeSelectFeatureSwitches())
  const expenseContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.PROCURIFY,
      ContentTypeModels.EXPENSEREPORT
    )
  )
  const baseContentTypeId = expenseContentType?.id ?? 0

  const [approverParams, setApproverParams] = useState({
    department: 0,
    accountCodeIds: [] as number[],
    amount: 0,
  })

  const debouncedSetApproverParams = useMemo(
    () => debounce(setApproverParams, 1000),
    []
  )

  useEffect(() => {
    return () => {
      debouncedSetApproverParams.cancel()
    }
  }, [debouncedSetApproverParams])

  const {
    data: { approverOptions, autoSelectedApproverId },
    states: { isLoading: isApproverLoading },
  } = useGetRequestableApproversPresenter({
    ...approverParams,
    baseContentTypeId,
  })

  useEffect(() => {
    if (autoSelectedApproverId !== null) {
      formikRef.current?.setFieldValue('nextApproverId', autoSelectedApproverId)
    }
  }, [autoSelectedApproverId, formikRef])

  useEffect(() => {
    if (formData) {
      const departmentId =
        formData.draft?.departmentId ?? formData.defaultDepartment?.id ?? 0
      const accountCodeIds =
        formData.draft?.items
          ?.map((item) => item.account?.accountCode.id)
          .filter((id): id is number => id != null) ?? []
      const amount =
        formData.draft?.items.reduce((sum, item) => sum + item.amount, 0) ?? 0

      setApproverParams({
        department: departmentId,
        accountCodeIds,
        amount,
      })
    }
  }, [formData])

  const isAdvancedApprovalTriggersEnabled =
    !!featureSwitches.advanced_approvals_additional_triggers

  const handleDepartmentChange = useCallback(
    (departmentId: number) => {
      debouncedSetApproverParams((prev) => ({
        ...prev,
        department: departmentId,
      }))
    },
    [debouncedSetApproverParams]
  )

  const handleAmountChange = useCallback(
    (amount: number) => {
      debouncedSetApproverParams((prev) => ({ ...prev, amount }))
    },
    [debouncedSetApproverParams]
  )

  const handleAccountCodesChange = useCallback(
    (accountCodeIds: number[]) => {
      if (isAdvancedApprovalTriggersEnabled) {
        debouncedSetApproverParams((prev) => ({ ...prev, accountCodeIds }))
      }
    },
    [debouncedSetApproverParams, isAdvancedApprovalTriggersEnabled]
  )

  return {
    approverOptions,
    isApproverLoading,
    onDepartmentChange: handleDepartmentChange,
    onAmountChange: handleAmountChange,
    onAccountCodesChange: handleAccountCodesChange,
  }
}
